import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tag, Loader2, Plus, X } from "lucide-react";

// PROMPT 116 (2026-09-23): nombres de producto que el sistema reconoce como este tour.
// Se aprenden solos cuando en la Bandeja se le pone el tour a una reserva que llego sin
// tour; aca se ven, se agregan a mano y se borran si se aprendio algo equivocado.
export default function NombresProductoDialog({ open, onOpenChange, tour, onSaved }) {
  const [nombres, setNombres] = useState([]);
  const [nuevo, setNuevo] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setNombres(tour?.alias_producto || []);
      setNuevo("");
    }
  }, [open, tour]);

  const agregar = async () => {
    if (!tour?.id || !nuevo.trim()) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/tours/${tour.id}/nombres-producto`, { texto: nuevo });
      setNombres(data?.tour?.alias_producto || []);
      setNuevo("");
      const n = data?.reservas_actualizadas || 0;
      toast.success(
        n
          ? `Guardado. ${n} reserva(s) de la Bandeja ya quedaron con este tour.`
          : "Guardado. Los próximos correos con ese nombre entran con este tour."
      );
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const quitar = async (texto) => {
    if (!tour?.id) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/tours/${tour.id}/nombres-producto/quitar`, { texto });
      setNombres(data?.alias_producto || []);
      toast.success("Nombre borrado.");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="nombres-producto-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Tag className="h-5 w-5" /> Nombres en los correos · {tour?.nombre}
          </DialogTitle>
          <DialogDescription>
            Cuando un correo trae uno de estos nombres de producto, la reserva entra con este tour. Se aprenden solos
            cuando en la Bandeja le ponés el tour a una reserva que llegó sin tour. Tiene que ser el nombre completo,
            tal como viene en el correo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {nombres.length === 0 ? (
            <p className="text-sm text-slate-500">Todavía no hay nombres aprendidos para este tour.</p>
          ) : (
            <ul className="space-y-1" data-testid="nombres-producto-lista">
              {nombres.map((n) => (
                <li key={n} className="flex items-center justify-between gap-2 rounded border px-3 py-1.5 text-sm">
                  <span className="break-all">{n}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-slate-400 hover:text-red-600"
                    disabled={saving}
                    onClick={() => quitar(n)}
                    title="Borrar este nombre"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 pt-2">
            <Input
              value={nuevo}
              onChange={(e) => setNuevo(e.target.value)}
              placeholder="From NY: Philadelphia day Tour and Lancaster Pennsylvania"
              data-testid="nombres-producto-input"
            />
            <Button onClick={agregar} disabled={saving || !nuevo.trim()} className="gap-1" data-testid="nombres-producto-agregar">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Agregar
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
